'use client'

import { useAuthState } from '@/app/providers/auth'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import { mobileMediaQuery } from '@caterdesk/ui--theme'
import { TextExtraSmall } from '@caterdesk/ui--typography'
import { amountToPriceString } from '@caterdesk/utils--money'
import {
  Agriculture,
  Close,
  Favorite,
  FavoriteBorder,
  Info,
  Star,
  Warning,
} from '@mui/icons-material'
import {
  alpha,
  Avatar,
  Box,
  Button,
  Dialog,
  Divider,
  GridLegacy as Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'

import sortBy from 'lodash/sortBy'
import Image from 'next/image'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { useMedia } from 'react-use'

import { roundRating } from '../../../common/utils/rating'
import type { MenuVendor } from '../../domain/menu-vendor'
import { useVendorHeader } from './useVendorHeader'

interface VendorHeaderProps {
  vendor: MenuVendor | null
  rating: number | null
}

export const VendorHeader: React.FC<VendorHeaderProps> = ({ vendor }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const tenant = useTenant()
  const gmRatingDisplayEnabled = useFeatureFlag(SPLITS.GM_ORDER_RATING_DISPLAY)

  const authState = useAuthState()

  const displayRating = gmRatingDisplayEnabled
    ? (vendor?.gmTotalRatings ?? 0) > 0
      ? roundRating(vendor?.gmRating ?? 0)
      : null
    : vendor?.rating
  const displayRatingCount = gmRatingDisplayEnabled ? vendor?.gmTotalRatings : vendor?.totalRatings

  const {
    modalOpen,
    vendorLoading,
    toggleFavourite,
    openModal,
    closeModal,
    isMyFavourite,
    minOrderValueExTax,
    deliveryFeeExTax,
    minNoticePeriod,
  } = useVendorHeader(vendor)

  const isMobileScreen = useMedia(mobileMediaQuery, false)

  const infoUrlData = vendor?.externalUrlData.data.find((url) => url?.tags.data.includes('default'))
  const infoUrl = infoUrlData?.url || ''
  const infoCopy = infoUrlData?.copy || ''
  const vendorImage = sortBy(vendor?.images || [], 'position')[0]
  const {
    logo,
    descriptionFull = '',
    descriptionShort = '',
    quote,
    sourcing,
    companyName: title,
    keywords = '',
  } = vendor || {}
  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={9}>
        <Box
          sx={{
            backgroundImage: vendorImage
              ? `url(${vendorImage.secureUrl})`
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: 'white',
            height: '100%',
            minHeight: 260,
            borderRadius: 2,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: 2,
            },
          }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {displayRating ? (
              <Tooltip title={gmRatingDisplayEnabled ? t('marketplace:ratingTooltip') : ''}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    px: 2,
                    py: 1,
                    borderRadius: '8px 0px 8px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}>
                  <Star sx={{ fontSize: 16 }} />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                    color={'primary.contrastText'}>
                    {displayRating} ({displayRatingCount})
                  </Typography>
                </Box>
              </Tooltip>
            ) : null}
            {vendor?.logo && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  px: 2,
                  py: 1,
                  borderRadius: '8px 8px 0 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}>
                <Avatar
                  src={vendor.logo}
                  alt={title}
                  sx={{
                    width: 84,
                    height: 84,
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={3}>
        <Box data-cy="supplierCard">
          <Typography variant="h4" component="h1" gutterBottom>
            {title}
          </Typography>

          <Stack spacing={1}>
            <Typography variant="body3" color="text.primary" fontWeight={500}>
              {keywords}
            </Typography>

            {descriptionShort && (
              <Typography variant="body3" color="text.secondary" sx={{ mt: 2 }}>
                {descriptionShort}
              </Typography>
            )}
          </Stack>

          <Stack sx={{ mt: 2 }} spacing={1}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {deliveryFeeExTax ? (
                isMobileScreen ? (
                  <TextExtraSmall style={{ fontWeight: 500 }}>{`${amountToPriceString(
                    vendor?.currency || '',
                    deliveryFeeExTax,
                    true,
                  )} ${t('marketplace:delivery')}`}</TextExtraSmall>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" fontWeight={400}>
                      {`${amountToPriceString(vendor?.currency || '', deliveryFeeExTax, true)} ${t('marketplace:delivery')}`}{' '}
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                  </>
                )
              ) : null}

              {minOrderValueExTax &&
                (isMobileScreen ? (
                  <TextExtraSmall style={{ fontWeight: 500 }}>{`${amountToPriceString(
                    vendor?.currency || '',
                    minOrderValueExTax,
                    true,
                  )} ${t('marketplace:minimum')}`}</TextExtraSmall>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontWeight={400}>
                    {`${amountToPriceString(vendor?.currency || '', minOrderValueExTax, true)} ${t('marketplace:minimum')}`}{' '}
                  </Typography>
                ))}
            </Box>
          </Stack>
          {minNoticePeriod ? (
            <Stack spacing={1} direction="row" marginY={1} alignItems="center">
              <Stack alignItems="center" direction="row" flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  {t('marketplace:ordersMustBePlaced')}
                </Typography>
                <Typography
                  variant="caption"
                  color="info.main"
                  sx={{ margin: '0 0.3rem 0 0.3rem' }}>
                  {minNoticePeriod} {t('marketplace:hours')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('marketplace:inAdvance')}
                </Typography>
              </Stack>
            </Stack>
          ) : null}

          {infoCopy && infoUrl ? (
            <Stack
              marginTop={0.5}
              spacing={1}
              direction="row"
              alignContent="center"
              alignItems="center">
              <Warning fontSize="medium" htmlColor={theme.palette.secondary.light} />
              <Typography variant="body2" color="text.disabled">
                {infoCopy} [
                <Link href={infoUrl} target="_blank">
                  {t('marketplace:linkHere')}
                </Link>
                ]
              </Typography>
            </Stack>
          ) : null}

          <Stack>
            {authState.type === 'authenticated' && (
              <Button
                startIcon={
                  isMyFavourite ? (
                    <Favorite data-testid="HeartIcon" />
                  ) : (
                    <FavoriteBorder data-testid="HeartIcon" />
                  )
                }
                variant="text"
                onClick={toggleFavourite}
                disabled={vendorLoading}
                sx={{
                  alignSelf: 'flex-start',
                  color: isMyFavourite ? 'error.main' : 'text.secondary',
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  '&:hover': {
                    color: 'error.main',
                    backgroundColor: 'transparent',
                  },
                  paddingX: 0,
                }}
                aria-label={t('marketplace:saveAsFavorite')}>
                <Typography variant="body3" fontWeight={500}>
                  {isMyFavourite
                    ? t('marketplace:removeFromFavorites', 'Remove from favorites')
                    : t('marketplace:saveAsFavorite')}
                </Typography>
              </Button>
            )}

            <Button
              startIcon={<Info />}
              variant="text"
              onClick={openModal}
              sx={{
                alignSelf: 'flex-start',
                color: 'text.secondary',
                justifyContent: 'flex-start',
                textTransform: 'none',
                paddingX: 0,
                '&:hover': {
                  color: 'info.main',
                  backgroundColor: 'transparent',
                },
              }}
              aria-label={t('marketplace:moreInformation')}>
              <Typography variant="body3" fontWeight={500}>
                {t('marketplace:moreInformation')}
              </Typography>
            </Button>
          </Stack>
        </Box>
      </Grid>
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        maxWidth="md"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 4,
          },
        }}>
        <IconButton
          aria-label="close"
          onClick={closeModal}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}>
          <Close />
        </IconButton>
        <Grid container spacing={2} padding={2}>
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              minHeight: { xs: 200, md: 400 },
            }}>
            <Image
              src={vendorImage?.secureUrl || '/images/placeholder-image.png'}
              alt={title ?? 'Background Image'}
              width={800}
              height={600}
              quality={100}
              priority
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '16px',
              }}
            />
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: 'none',
                flex: 1,
                maxHeight: { xs: 'auto', md: 600 },
              }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                {logo && (
                  <Avatar
                    src={logo}
                    alt={title}
                    sx={{
                      width: 50,
                      height: 50,
                    }}
                  />
                )}
                <Typography variant="h5" component="h2" fontWeight="bold">
                  {title}
                </Typography>
              </Stack>

              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  paddingRight: 1,
                }}>
                <Typography
                  variant="body3"
                  color="text.secondary"
                  display="block"
                  sx={{
                    pb: 2,
                    lineHeight: 1.5,
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    hyphens: 'auto',
                  }}>
                  {descriptionFull}
                </Typography>

                {quote && (
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: alpha(theme.palette.error.main, 0.2),
                      padding: 2,
                      mb: 2,
                      borderRadius: 2,
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                    }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Favorite sx={{ color: 'error.main', fontSize: 18 }} />
                      <Typography variant="body3" fontWeight="600" color="error.dark">
                        {t('marketplace:handpickedBy', { tenantName: tenant.name })}
                      </Typography>
                    </Stack>
                    <Typography variant="body3" color="error.dark">
                      {quote}
                    </Typography>
                  </Paper>
                )}

                {sourcing && (
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      padding: 2,
                      borderRadius: 2,
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                    }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Agriculture sx={{ color: 'primary.main', fontSize: 18 }} />
                      <Typography variant="body3" fontWeight="600" color="primary.dark">
                        {t('marketplace:sourcedResponsibly')}
                      </Typography>
                    </Stack>
                    <Typography variant="body3" color="primary.dark">
                      {sourcing}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Dialog>
    </Grid>
  )
}
